import { Fragment } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import { ChevronRight, ExpandMore, Folder } from '@mui/icons-material';
import { FileWithStatus } from '@/types/upload';
import { FileStatusIcon, FileErrorMessage } from '@/components/Upload/components/UploadComponents';
import { FolderGroup, getFolderSummary, UploadStatusTabs } from '@/components/Upload/utils/uploadUtils';
import { UploadTestIds } from '@/enums/uploadTestIds.enum';

const MODAL_MIN_HEIGHT = 400;
const FOLDER_ROW_HEIGHT = 53;

const SecondaryText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const UploadFileRow = ({ file, folderPath }: { file: FileWithStatus; folderPath: string }) => {
  return (
    <TableRow key={file.path} data-testid={`${UploadTestIds.UploadTableRow}-${file.path}`}>
      <TableCell sx={{ pl: folderPath ? 4 : 2 }}>
        <FileStatusIcon file={file} />
      </TableCell>
      <TableCell sx={{ pl: folderPath ? 4 : 2 }}>{folderPath ? file.name : file.path}</TableCell>
      <TableCell>
        <FileErrorMessage file={file} />
      </TableCell>
    </TableRow>
  );
};

const UploadFolderRow = ({
  group,
  collapsed,
  onToggle,
}: {
  group: FolderGroup;
  collapsed: boolean;
  onToggle: (folderPath: string) => void;
}) => {
  return (
    <TableRow
      data-testid={`${UploadTestIds.UploadFolderRow}-${group.folderPath}`}
      onClick={() => onToggle(group.folderPath)}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
        height: FOLDER_ROW_HEIGHT,
      }}
    >
      <TableCell sx={{ height: FOLDER_ROW_HEIGHT }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {collapsed ? <ChevronRight fontSize='small' /> : <ExpandMore fontSize='small' />}
          <Folder fontSize='small' color='action' />
        </Box>
      </TableCell>
      <TableCell sx={{ fontWeight: 600, height: FOLDER_ROW_HEIGHT }}>{group.folderPath}</TableCell>
      <TableCell sx={{ height: FOLDER_ROW_HEIGHT }}>
        <SecondaryText>{getFolderSummary(group.stats)}</SecondaryText>
      </TableCell>
    </TableRow>
  );
};

const UploadStatusEmptyState = ({
  filter,
  getMessage,
}: {
  filter: UploadStatusTabs;
  getMessage: (filter: UploadStatusTabs) => string;
}) => {
  return (
    <Box
      sx={{
        mt: 2,
        minHeight: MODAL_MIN_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-testid={UploadTestIds.UploadStatusEmptyState}
    >
      <Typography variant='body1' color='text.secondary'>
        {getMessage(filter)}
      </Typography>
    </Box>
  );
};

export const UploadStatusTable = ({
  folderGroups,
  collapsedFolders,
  toggleFolder,
  isEmpty = false,
  emptyStateFilter,
  getEmptyMessage,
}: {
  folderGroups: FolderGroup[];
  collapsedFolders: Set<string>;
  toggleFolder: (folderPath: string) => void;
  isEmpty?: boolean;
  emptyStateFilter?: UploadStatusTabs;
  getEmptyMessage?: (filter: UploadStatusTabs) => string;
}) => {
  if (isEmpty && emptyStateFilter && getEmptyMessage) {
    return <UploadStatusEmptyState filter={emptyStateFilter} getMessage={getEmptyMessage} />;
  }

  return (
    <TableContainer sx={{ mt: 2, minHeight: MODAL_MIN_HEIGHT, overflow: 'auto' }}>
      <Table aria-label='Upload status table'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '60px' }}>Status</TableCell>
            <TableCell>File</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {folderGroups.map(group => (
            <Fragment key={group.folderPath || 'root'}>
              {group.folderPath && (
                <UploadFolderRow
                  group={group}
                  collapsed={collapsedFolders.has(group.folderPath)}
                  onToggle={toggleFolder}
                />
              )}
              {(!group.folderPath || !collapsedFolders.has(group.folderPath)) &&
                group.files.map(file => <UploadFileRow key={file.path} file={file} folderPath={group.folderPath} />)}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
